import React from 'react';
import _ from 'lodash';
import style from './MainMedia.scss';
import classNames from 'classnames';
import {ImageMode, ImageModeValues} from '@wix/wixstores-client-core/dist/es/src/media/constants';
import {
  adjustImageDimensionToContainer,
  convertConfigToNumber,
  getMediaUrl,
  isSantaFullWidthMode,
  isVideo,
} from '@wix/wixstores-client-core/dist/es/src/media/mediaService';
import {OldProductThumbnail} from '@wix/wixstores-client-common-components/dist/es/src/OldProductThumbnail';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {ProductEmptyImage} from './ProductEmptyImage/ProductEmptyImage';
import {ProductVideoItem} from './ProductVideoItem/ProductVideoItem';
import {DimensionsConfig, IMediaItem, IProductDTO} from '../../../types/app-types';
import {ProductGalleryContext} from '../ProductGalleryProvider/ProductGalleryProvider';
import {DEFAULT_IMAGE_SIZE, imageMobileWidth} from '../../../constants';
import {ProductImage} from './ProductImage/ProductImage';
import {MainMediaSwiper} from './MainMediaSwiper/MainMediaSwiper';
import {SPECS} from '../../../specs';

export interface MainMediaProps extends ProvidedGlobalProps {
  imageMode: ImageMode;
  product: IProductDTO;
  swipeToScroll: boolean;
  layoutDimensions: DimensionsConfig;
  withDynamicHeight: boolean;
  isZoomModalEnabled: boolean;
}

export interface MainMediaState {
  width: number;
  height: number;
  mountedDOM: boolean;
  numOfLoadedImages: number;
  mobileHeight: number;
  mobileWidth: number;
}

export enum MainMediaDataHook {
  Video = 'product-page-media-video',
  ImageWrapper = 'main-media-image-wrapper',
  MissingMedia = 'product-page-missing-media',
  Slider = 'product-page-media-slider',
  Root = 'main-media',
}
class MainMediaComponent extends React.Component<MainMediaProps, MainMediaState> {
  public state = {
    width: 0,
    height: 0,
    mobileHeight:
      this.props.product.media.length > 0
        ? (imageMobileWidth * this.props.product.media[0].height) / this.props.product.media[0].width
        : imageMobileWidth,
    mobileWidth: imageMobileWidth,
    mountedDOM: false,
    numOfLoadedImages: 0,
  };

  private _divRef: HTMLDivElement;

  public componentDidMount(): void {
    const {clientHeight, clientWidth} = this._divRef;
    this.setState({
      height: clientHeight,
      width: clientWidth,
      mountedDOM: true,
    });
  }

  /* istanbul ignore next: todo test */
  public componentDidUpdate(prevProps: Readonly<MainMediaProps>, prevState: Readonly<MainMediaState>): void {
    if (prevState.numOfLoadedImages !== this.state.numOfLoadedImages) {
      const {clientHeight, clientWidth} = this._divRef;
      this.setState({height: clientHeight, width: clientWidth, mountedDOM: true});
    }
  }

  private getContainerDOMDimensions(): {width: number; height: number} {
    const {
      layoutDimensions,
      globals: {
        dimensions: {height, width},
      },
    } = this.props;
    const {widthConf, heightConf} = layoutDimensions;

    if (this.props.globals.isMobile) {
      return {
        width: this.state.mobileWidth,
        height: this.state.mobileHeight,
      };
    }
    if (this.state.mountedDOM) {
      return _.pick(this.state, 'width', 'height');
    }
    /* istanbul ignore if: can't test this case */
    if (isSantaFullWidthMode(this.props.globals)) {
      widthConf.num = 980;
      widthConf.unit = 'px';
    }

    return {
      width: widthConf ? convertConfigToNumber(widthConf, width) : /* istanbul ignore next */ 0,
      height: heightConf ? convertConfigToNumber(heightConf, height) : /* istanbul ignore next */ 0,
    };
  }

  private getZoomMainImageUrl(
    media: IMediaItem,
    container: {width: number; height: number},
    imageMode: ImageMode
  ): string {
    const target = adjustImageDimensionToContainer(media, container, imageMode, {upsclae: true});
    const bigger = _.mapValues(target, (v) => v * 2);
    return getMediaUrl(media, bigger, {
      isSSR: false,
      imageMode,
    });
  }

  /* istanbul ignore next: todo: test */
  private readonly imageLoaded = () => {
    const nextNumOfLoadedImages = this.state.numOfLoadedImages + 1;
    this.setState({numOfLoadedImages: nextNumOfLoadedImages});
  };

  private getMediaItemToUse(media: IMediaItem, imageRatioId: number) {
    const {
      imageMode,
      product: {media: allMedia},
    } = this.props;
    const isImageFit = imageMode === ImageModeValues.FIT;
    const isImageAuto = imageRatioId === -1;

    if (!isImageAuto || isImageFit) {
      return media;
    }

    return {
      ...media,
      width: allMedia[0].width,
      height: allMedia[0].height,
    };
  }

  private getImage(media: IMediaItem, index: number) {
    const {
      product: {name},
      imageMode,
      isZoomModalEnabled,
    } = this.props;

    return (
      <ProductGalleryContext.Consumer>
        {({selectedIndex, imageRatioId}) => {
          const mediaItemToUse = this.getMediaItemToUse(media, imageRatioId);
          return (
            <div className={style.productImage} key={media.url}>
              <ProductImage
                stretchImage={true}
                mediaItem={mediaItemToUse}
                imageMode={imageMode}
                productName={name}
                fluid={true}
                imageLoaded={this.imageLoaded}
                isSelected={selectedIndex === index}
                isZoomModalEnabled={isZoomModalEnabled}
              />
            </div>
          );
        }}
      </ProductGalleryContext.Consumer>
    );
  }

  private getVideoPosterUrl(media: IMediaItem): string {
    const {imageMode} = this.props;
    const containerDomDimensions = this.getContainerDOMDimensions();
    const mediaDimensions = adjustImageDimensionToContainer(media, containerDomDimensions, ImageModeValues.CROP);
    return getMediaUrl(media, mediaDimensions, {
      isSSR: true,
      imageMode,
    });
  }

  // TODO: Extract poster rendering to a shared VideoPoster component
  // to ensure consistent behavior between SSR and client, preventing CLS
  private getVideoPosterItem(media: IMediaItem, index: number, posterUrl: string): JSX.Element {
    const containerDomDimensions = this.getContainerDOMDimensions();
    const mediaDimensions = adjustImageDimensionToContainer(media, containerDomDimensions, ImageModeValues.CROP);

    // Use same structure and dimensions as ProductVideoItem to prevent CLS
    // Inline styles are intentional for SSR: ensures correct rendering on first paint
    // without waiting for CSS to load, critical for LCP optimization
    return (
      <div key={`media-${index}`} className={style.mediaVideoContainer}>
        <div
          data-hook={MainMediaDataHook.Video}
          style={{
            height: mediaDimensions.height,
            width: mediaDimensions.width,
            margin: '0 auto',
            position: 'relative',
            zIndex: 1,
          }}>
          <img
            src={posterUrl}
            width={mediaDimensions.width}
            height={mediaDimensions.height}
            style={{width: '100%', height: '100%', objectFit: 'cover'}}
            alt=""
            {...{fetchpriority: 'high'}}
          />
        </div>
      </div>
    );
  }

  private getVideoItem(media: IMediaItem, index: number, posterUrl?: string): JSX.Element {
    const video = media.videoFiles[0];
    /* istanbul ignore if: todo: test */
    if (!video) {
      return this.getImageItem(media, index);
    }
    const mediaDimensions = adjustImageDimensionToContainer(
      media,
      this.getContainerDOMDimensions(),
      ImageModeValues.CROP
    );

    return (
      <ProductGalleryContext.Consumer>
        {({registerVideoPlayer, selectedIndex}) => {
          const isCurrentVideoSelected = selectedIndex === index;
          const mediaVideoContainerClasses = classNames(style.mediaVideoContainer, {
            [style.mediaVideoContainerUnselected]: !isCurrentVideoSelected,
          });

          return (
            <div key={`media-${index}`} className={mediaVideoContainerClasses}>
              <ProductVideoItem
                dimensions={mediaDimensions}
                index={index}
                onAddVideo={registerVideoPlayer}
                video={video}
                posterUrl={posterUrl}
                mediaItem={media}
                imageMode={this.props.imageMode}
                mountedToDOM={this.state.mountedDOM}
              />
            </div>
          );
        }}
      </ProductGalleryContext.Consumer>
    );
  }

  private getImageItem(media: IMediaItem, index: number): JSX.Element {
    const {imageMode} = this.props;
    const {mountedDOM} = this.state;
    const containerDomDimensions = this.getContainerDOMDimensions();
    const renderEmptyImage = index > 0 && !mountedDOM;
    const body = React.createElement(
      'div',
      {
        className: classNames([style.mediaWrapper, 'media-wrapper-hook', style.defaultWidth]),
        href: this.getZoomMainImageUrl(media, containerDomDimensions, imageMode),
        id: 'get-image-item-id',
        style: {height: containerDomDimensions.height},
      },
      renderEmptyImage ? <ProductEmptyImage /> : this.getImage(media, index)
    );

    return (
      <div
        data-hook={MainMediaDataHook.ImageWrapper}
        className="main-media-image-wrapper-hook"
        key={`main-media-image-${index}`}>
        {body}
      </div>
    );
  }

  private getMediaItem(mediaItem: IMediaItem, index: number): JSX.Element {
    const {mountedDOM} = this.state;
    const isVideoItem = isVideo(mediaItem);
    const isVideoPosterOptimizationEnabled = this.props.globals.useExperiments.enabled(
      SPECS.ProductPageVideoPosterOptimization
    );

    if (isVideoItem) {
      if (isVideoPosterOptimizationEnabled) {
        const posterUrl = this.getVideoPosterUrl(mediaItem);

        return mountedDOM
          ? this.getVideoItem(mediaItem, index, posterUrl)
          : this.getVideoPosterItem(mediaItem, index, posterUrl);
      }
      return mountedDOM ? this.getVideoItem(mediaItem, index) : this.getImageItem(mediaItem, index);
    }

    return this.getImageItem(mediaItem, index);
  }

  private getMediaItems() {
    const {
      product: {media},
    } = this.props;

    return _.map(media, (mediaItem, index) => this.getMediaItem(mediaItem, index));
  }

  private getNoMediaMode(): JSX.Element {
    const {product} = this.props;
    const containerDomDimensions = this.getContainerDOMDimensions();
    /* istanbul ignore next:tested in sled */
    return (
      <div
        data-hook={MainMediaDataHook.MissingMedia}
        style={{height: containerDomDimensions.height}}
        className={style.defaultWidth}>
        <OldProductThumbnail
          className={style.NoMediaThumbnail}
          height={containerDomDimensions.height}
          product={product}
          defaultImageSize={DEFAULT_IMAGE_SIZE}
          getResizedImageUrl={null}
          useNewDefaultProductImage={true}
          useDynamicDefaultImageSize={this.props.globals.experiments.EnableDynamicSizeDefaultImage}
        />
      </div>
    );
  }

  private getMediaSlider(): JSX.Element {
    return (
      <ProductGalleryContext.Consumer>
        {({setMainMediaSwiper}) => (
          <MainMediaSwiper
            media={this.getMediaItems()}
            swipeToScroll={this.props.swipeToScroll}
            isMobile={this.props.globals.isMobile}
            setSwiper={setMainMediaSwiper}
          />
        )}
      </ProductGalleryContext.Consumer>
    );
  }

  public render(): JSX.Element {
    const {
      product: {media},
      globals: {useExperiments},
    } = this.props;
    const isNavigationPerBreakpointEnabled = useExperiments.enabled(SPECS.PPNavigationSectionPerBreakpoint);
    return (
      <div
        data-hook={MainMediaDataHook.Root}
        className={classNames(style.mainMedia, {
          [style.isNavigationPerBreakpointEnabled]: isNavigationPerBreakpointEnabled,
        })}
        ref={(r) => (this._divRef = r)}>
        {_.isEmpty(media) ? this.getNoMediaMode() : this.getMediaSlider()}
      </div>
    );
  }
}

export const MainMedia = withGlobalProps(MainMediaComponent);
export {MainMediaComponent};
